/**
 * FloatingSavePill — fixed-position save button for TikTok Listing editing.
 *
 * Appears when any field is dirty. Shows spinner while saving,
 * success message after save, error with retry on failure.
 * Also contains a "Discard" button to reset all dirty fields.
 */

import { useEffect, useRef } from "react";
import { Loader2, Check, AlertCircle } from "lucide-react";
import {
  useTikTokEditStore,
  selectIsDirty,
  selectDirtyCount,
} from "../stores/useTikTokEditStore";
import { useTikTokSave } from "../hooks/useTikTokSave";

export function FloatingSavePill() {
  const isDirty = useTikTokEditStore(selectIsDirty);
  const dirtyCount = useTikTokEditStore(selectDirtyCount);
  const saveStatus = useTikTokEditStore((s) => s.saveStatus);
  const saveMessage = useTikTokEditStore((s) => s.saveMessage);
  const resetAll = useTikTokEditStore((s) => s.resetAll);
  const setSaveStatus = useTikTokEditStore((s) => s.setSaveStatus);

  const { mutate: save } = useTikTokSave();
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-fade success message after 2.5s
  useEffect(() => {
    if (saveStatus === "success") {
      fadeTimerRef.current = setTimeout(() => {
        setSaveStatus("idle");
      }, 2500);
    }
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [saveStatus, setSaveStatus]);

  // Navigation guard: warn on tab close if dirty
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useTikTokEditStore.getState().dirtyFields &&
          Object.keys(useTikTokEditStore.getState().dirtyFields).length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Nothing to show
  const visible = isDirty || saveStatus === "saving" || saveStatus === "success" || saveStatus === "error";
  if (!visible) return null;

  return (
    <div
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        flex items-center gap-3
        rounded-full px-5 py-3 shadow-lg
        transition-all duration-300 ease-out
        ${saveStatus === "error" ? "bg-red-600 text-white" : "bg-primary text-primary-foreground"}
        ${visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
      `}
    >
      {/* ── Saving state ── */}
      {saveStatus === "saving" && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">Saving...</span>
        </>
      )}

      {/* ── Success state ── */}
      {saveStatus === "success" && (
        <>
          <Check className="h-4 w-4" />
          <span className="text-sm font-medium">{saveMessage || "Saved"}</span>
        </>
      )}

      {/* ── Error state ── */}
      {saveStatus === "error" && (
        <button
          type="button"
          className="flex items-center gap-2"
          onClick={() => save()}
        >
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm font-medium">
            {saveMessage || "Save failed — tap to retry"}
          </span>
        </button>
      )}

      {/* ── Idle (dirty) state ── */}
      {saveStatus === "idle" && isDirty && (
        <>
          <button
            type="button"
            className="flex items-center gap-2 font-medium text-sm"
            onClick={() => save()}
          >
            Save changes
            {dirtyCount > 1 && (
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] rounded-full bg-primary-foreground/20 px-1.5 text-xs">
                {dirtyCount}
              </span>
            )}
          </button>
          <span className="w-px h-4 bg-primary-foreground/30" />
          <button
            type="button"
            className="text-sm text-primary-foreground/70 hover:text-primary-foreground"
            onClick={() => resetAll()}
          >
            Discard
          </button>
        </>
      )}
    </div>
  );
}
